/**
 * Utility functions for handling match status and display
 */

export interface MatchStatusInfo {
  displayText: string
  isLive: boolean
  color: string
  elapsed?: number | null
}

/**
 * Get formatted match status display text
 */
export function getMatchStatusDisplay(
  status_short: string,
  status_long: string,
  elapsed: number | null
): MatchStatusInfo {
  
  switch (status_short) {
    case 'NS':
      return {
        displayText: 'Chưa diễn ra',
        isLive: false,
        color: '#666'
      }
    
    case '1H':
      return {
        displayText: elapsed ? `Hiệp 1: ${elapsed}'` : 'Hiệp 1',
        isLive: true,
        color: '#2ecc71',
        elapsed
      }
    
    case '2H':
      return {
        displayText: elapsed ? `Hiệp 2: ${elapsed}'` : 'Hiệp 2',
        isLive: true,
        color: '#2ecc71',
        elapsed
      }
    
    case 'HT':
      return {
        displayText: 'Giải lao',
        isLive: true,
        color: '#f39c12'
      }
    
    case 'ET':
      return {
        displayText: elapsed ? `Hiệp phụ: ${elapsed}'` : 'Hiệp phụ',
        isLive: true,
        color: '#e74c3c',
        elapsed
      }
    
    case 'BT':
      return {
        displayText: 'Nghỉ giữa hiệp phụ',
        isLive: true,
        color: '#f39c12'
      }
    
    case 'P':
      return {
        displayText: 'Luân lưu',
        isLive: true,
        color: '#e74c3c'
      }
    
    case 'FT':
      return {
        displayText: 'Đã kết thúc',
        isLive: false,
        color: '#95a5a6'
      }
    
    case 'AET':
      return {
        displayText: 'KT hiệp phụ',
        isLive: false,
        color: '#95a5a6'
      }
    
    case 'PEN':
      return {
        displayText: 'KT luân lưu',
        isLive: false,
        color: '#95a5a6'
      }
    
    case 'SUSP':
      return {
        displayText: 'Hoãn',
        isLive: false,
        color: '#e67e22'
      }
    
    case 'INT':
      return {
        displayText: 'Gián đoạn',
        isLive: true,
        color: '#e67e22'
      }
    
    case 'PST':
      return {
        displayText: 'Hoãn',
        isLive: false,
        color: '#e67e22'
      }
    
    case 'CANC':
      return {
        displayText: 'Hủy',
        isLive: false,
        color: '#e74c3c'
      }
    
    case 'ABD':
      return {
        displayText: 'Hủy bỏ',
        isLive: false,
        color: '#e74c3c'
      }
    
    case 'AWD':
      return {
        displayText: 'Trao giải',
        isLive: false,
        color: '#9b59b6'
      }
    
    case 'WO':
      return {
        displayText: 'Walk over',
        isLive: false,
        color: '#9b59b6'
      }
    
    case 'LIVE':
      return {
        displayText: elapsed ? `Đang diễn ra: ${elapsed}'` : 'Đang diễn ra',
        isLive: true,
        color: '#2ecc71',
        elapsed
      }
    
    default:
      // Fallback to status_long or status_short
      return {
        displayText: status_long || status_short || 'Không rõ',
        isLive: ['1H', '2H', 'HT', 'ET', 'BT', 'P', 'LIVE', 'INT'].includes(status_short),
        color: '#666'
      }
  }
}

/**
 * Get simple live/finished/upcoming status
 */
export function getSimpleMatchStatus(status_short: string): 'live' | 'finished' | 'upcoming' {
  const liveStatuses = ['1H', '2H', 'HT', 'ET', 'BT', 'P', 'LIVE', 'INT']
  const finishedStatuses = ['FT', 'AET', 'PEN', 'AWD', 'WO', 'ABD', 'CANC']
  
  if (liveStatuses.includes(status_short)) {
    return 'live'
  } else if (finishedStatuses.includes(status_short)) {
    return 'finished'
  } else {
    return 'upcoming'
  }
}

/**
 * Check if match is currently live
 */
export function isMatchLive(status_short: string): boolean {
  return ['1H', '2H', 'HT', 'ET', 'BT', 'P', 'LIVE', 'INT'].includes(status_short)
}
